import React, {useEffect, useRef} from 'react'
import * as d3 from 'd3'
import PropTypes from 'prop-types'
import {fontSizeExtraLarge, fontSizeExtraSmall, mediumFontWeight, lightFontWeight} from '../../assets/styles/typography'
import {rxrGreyColor, rxrBlackColor, rxrDarkGreyColor} from '../../assets/styles/color'

const DEFAULT_ARC_DEGREES = 300
const MARGIN_BETWEEN_ARC_AND_LABELS = 20
const DEFAULT_MIN_MAX = 100
const DEFAULT_BOX_SIZE = 100
const ARC_THICKNESS = 5
const DEGREES_90 = Math.PI / 2

function degreesToRadians(degrees) {
  return (degrees * Math.PI) / 180
}

function ArcChart(props) {
  const ref = useRef()
  const score = isNaN(props.score) ? null : parseFloat(props.score)
  const labelFontSize = props.labelFontSize || fontSizeExtraSmall
  const arcDegrees = props.arcDegrees || DEFAULT_ARC_DEGREES
  const arcRadians = degreesToRadians(arcDegrees)
  const minValue = '' + (typeof props.minVal === 'number' ? props.minVal : -DEFAULT_MIN_MAX)
  const maxValue = '' + (typeof props.maxVal === 'number' ? props.maxVal : DEFAULT_MIN_MAX)
  const width = props.width || DEFAULT_BOX_SIZE

  const outerRadius = width / 2
  const innerRadius = outerRadius - ARC_THICKNESS
  const centerRadius = innerRadius + (outerRadius - innerRadius) / 2
  const diameter = outerRadius * 2

  // the width and height of the arc is calculated off the circle diameter (width) and the sweep
  const arcWidth = Math.ceil(arcDegrees >= 180 ? diameter : diameter * Math.sin(arcRadians / 2))
  const arcHeight = Math.ceil((diameter / 2) * (1 - Math.cos(arcRadians / 2)))

  // the final height of the svg is the height of the arc plus the padding and labels
  const height = arcHeight + MARGIN_BETWEEN_ARC_AND_LABELS + labelFontSize

  useEffect(() => {
    const container = d3.select(ref.current)
    // every run appends a fresh <g>, so without this the previous arc and score stay underneath the new one
    container.selectAll('*').remove()

    const svg = container
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2}, ${width / 2})`)

    const startAngle = -arcRadians / 2
    const endAngle = arcRadians / 2

    const backgroundArc = d3
      .arc()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .cornerRadius(ARC_THICKNESS / 2)
      .startAngle(startAngle)
      .endAngle(endAngle)

    svg.append('path').attr('d', backgroundArc).attr('fill', rxrGreyColor)

    if (typeof score === 'number') {
      const scoreAsAPercentage = (score - minValue) / (maxValue - minValue)
      const scoreAngle = startAngle + scoreAsAPercentage * (endAngle - startAngle)

      const scoreArc = d3
        .arc()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius)
        .cornerRadius(ARC_THICKNESS / 2)
        .startAngle(startAngle)
        .endAngle(scoreAngle)

      svg.append('path').attr('d', scoreArc).attr('fill', props.arcColor)
    }

    const textScore = score !== null ? score : 'N/A'

    // Main score value
    svg
      .append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('text-anchor', 'middle')
      .attr('dy', (arcHeight - outerRadius) / 3) // divided by 3 seems to work best, but in my head it should be / 2?
      .style('font-size', props.valueFontSize || fontSizeExtraLarge)
      .style('font-weight', mediumFontWeight)
      .style('fill', rxrBlackColor)
      .text(textScore)

    const startX = Math.cos(startAngle - DEGREES_90) * centerRadius
    const startY = Math.sin(startAngle - DEGREES_90) * centerRadius + MARGIN_BETWEEN_ARC_AND_LABELS

    // min value
    svg
      .append('text')
      .attr('x', startX)
      .attr('y', startY)
      .attr('text-anchor', 'middle')
      .style('font-size', labelFontSize)
      .style('font-weight', lightFontWeight)
      .style('fill', rxrDarkGreyColor)
      .text(`${minValue}`)

    const endX = Math.cos(endAngle - DEGREES_90) * centerRadius
    const endY = Math.sin(endAngle - DEGREES_90) * centerRadius + MARGIN_BETWEEN_ARC_AND_LABELS

    // max value
    svg
      .append('text')
      .attr('x', endX)
      .attr('y', endY)
      .attr('text-anchor', 'middle')
      .style('font-size', labelFontSize)
      .style('font-weight', lightFontWeight)
      .style('fill', rxrDarkGreyColor)
      .text(`${maxValue}`)
  }, [score, height, width, props.arcColor, minValue, maxValue])

  return <svg ref={ref}></svg>
}

ArcChart.propTypes = {
  score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minVal: PropTypes.number,
  maxVal: PropTypes.number,
  valueFontSize: PropTypes.number, // in px
  labelFontSize: PropTypes.number, // in px
  // height: PropTypes.number, the height is calculated based on the width, arcDegrees, and label sizes
  width: PropTypes.number, // the size the circle if arcDegrees is 360
  arcDegrees: PropTypes.number,
  arcColor: PropTypes.string,
}

export default ArcChart
